// Military icon cache with service-specific support
// Service codes: AF (Air Force), AR (Army), NY (Navy), CG (Coast Guard), MA (Marines), GV (Government)

// In-memory cache of available military icons
// Structure: { countryCode: { default: '/path/to/icon.svg', services: { AF: '/path', NY: '/path', ... } } }
const militaryIconCache = {};

// Flag to track if cache has been initialized
let cacheInitialized = false;

// ISO 3166-1 alpha-2 codes (lowercase) with a roundel under public/military/<cc>/.
// Sourced from Wikimedia Commons — see public/military/ATTRIBUTION.md. Keep this
// list in sync with the directories; actual availability is verified by image
// loading.
const KNOWN_COUNTRIES = [
  'ae', 'ao', 'ar', 'at', 'au', 'be', 'bg', 'bh', 'br', 'ca', 'ch', 'cl', 'co', 'cz', 'de',
  'dk', 'dz', 'ec', 'ee', 'eg', 'es', 'fi', 'fr', 'gb', 'gr', 'hr', 'hu', 'id', 'ie', 'il',
  'in', 'it', 'jo', 'jp', 'kr', 'kw', 'lt', 'lu', 'lv', 'ma', 'mx', 'my', 'ng', 'nl', 'no',
  'nz', 'pe', 'pk', 'pl', 'pt', 'qa', 'ro', 'sa', 'se', 'si', 'sn', 'th', 'tn', 'tr', 'ua',
  'us', 've', 'za'
];

// Service code mapping from directory names
const SERVICE_CODES = {
  'AF': 'AF', // Air Force
  'AR': 'AR', // Army
  'NY': 'NY', // Navy
  'CG': 'CG', // Coast Guard
  'MA': 'MA', // Marines
  'GV': 'GV', // Government
};

// Service-specific roundels that actually exist under public/military/<cc>/<SERVICE>/icon.svg.
// Only these are registered: an unverified path 404s, the <img> onError hides it, and the
// aircraft ends up with no mark at all (RAF aircraft lost the gb roundel this way).
const KNOWN_SERVICE_ICONS = {
  us: ['AF', 'NY'],
};

// Keywords to identify military services from operator names
const SERVICE_KEYWORDS = {
  'AF': ['air force', 'airforce', 'raf', 'luftwaffe', 'aeronautica', 'forca aerea', 'força aérea', 'ejercito del aire', 'armée de l\'air'],
  'AR': ['army', 'heer', 'esercito', 'ejercito', 'armée de terre', 'exército'],
  'NY': ['navy', 'naval', 'marine', 'marina', 'armada', 'marinha'],
  'CG': ['coast guard', 'coastguard', 'guardia costera', 'guardia costiera', 'garde-côtes', 'guarda costeira'],
  'MA': ['marines', 'marine corps', 'infanteria de marina', 'fanteria di marina'],
  'GV': ['government', 'gobierno', 'governo', 'gouvernement', 'regierung', 'state', 'police', 'policia', 'polizia', 'polizei'],
};

/**
 * Normalize country code to lowercase, handling special cases
 * @param {string} countryCode
 * @returns {string} Normalized country code
 */
function normalizeCountryCode(countryCode) {
  if (!countryCode) return '';
  let normalized = countryCode.toLowerCase();
  // The roundel directory is the ISO code 'gb'; accept the informal 'uk' too.
  // (This used to map gb -> uk, which pointed at a directory that does not
  // exist, so UK military aircraft never got their roundel.)
  if (normalized === 'uk') {
    normalized = 'gb';
  }
  return normalized;
}

/**
 * Detect military service from operator name
 * @param {string} operatorName - The operator/airline name
 * @returns {string|null} Service code (AF, AR, NY, CG, MA, GV) or null if not detected
 */
export function detectServiceFromOperator(operatorName) {
  if (!operatorName) return null;

  const lowerName = operatorName.toLowerCase();

  for (const [serviceCode, keywords] of Object.entries(SERVICE_KEYWORDS)) {
    for (const keyword of keywords) {
      if (lowerName.includes(keyword)) {
        return serviceCode;
      }
    }
  }

  return null;
}

/**
 * Build the icon path for a country and optional service
 * @param {string} countryCode - Normalized country code
 * @param {string|null} serviceCode - Service code or null for default
 * @returns {string} Icon path
 */
function buildIconPath(countryCode, serviceCode = null) {
  if (serviceCode) {
    return `/military/${countryCode}/${serviceCode}/icon.svg`;
  }
  // Handle Italy's special case (it.svg instead of icon.svg)
  if (countryCode === 'it') {
    return `/military/${countryCode}/it.svg`;
  }
  return `/military/${countryCode}/icon.svg`;
}

/**
 * Verify if an image exists by attempting to load it
 * @param {string} path - Image path to verify
 * @returns {Promise<boolean>} True if image exists
 */
async function verifyImageExists(path) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = path;
  });
}

/**
 * Initialize the military icon cache by checking which icons exist
 * This should be called once when the app starts
 */
export async function initializeMilitaryIconCache() {
  if (cacheInitialized) return;

  // console.log('🎖️ Initializing military icon cache...');

  for (const country of KNOWN_COUNTRIES) {
    const countryCache = {
      default: null,
      services: {},
    };

    // Check default icon
    const defaultPath = buildIconPath(country);
    if (await verifyImageExists(defaultPath)) {
      countryCache.default = defaultPath;
    }

    // Check service-specific icons
    for (const serviceCode of Object.keys(SERVICE_CODES)) {
      const servicePath = buildIconPath(country, serviceCode);
      if (await verifyImageExists(servicePath)) {
        countryCache.services[serviceCode] = servicePath;
      }
    }

    // Only add to cache if at least default icon exists
    if (countryCache.default) {
      militaryIconCache[country] = countryCache;
    }
  }

  cacheInitialized = true;
  // console.log('🎖️ Military icon cache initialized:', militaryIconCache);
}

/**
 * Synchronously initialize cache from the known-on-disk lists (no network check).
 * initializeMilitaryIconCache() can be called afterwards to verify by loading.
 */
export function initializeMilitaryIconCacheSync() {
  if (cacheInitialized) return;

  for (const country of KNOWN_COUNTRIES) {
    militaryIconCache[country] = {
      default: buildIconPath(country),
      services: {},
    };

    for (const serviceCode of KNOWN_SERVICE_ICONS[country] || []) {
      militaryIconCache[country].services[serviceCode] = buildIconPath(country, serviceCode);
    }
  }

  cacheInitialized = true;
}

/**
 * Check if a military logo is available for a country
 * @param {string} countryCode - Country code
 * @returns {boolean}
 */
export function hasMilitaryLogo(countryCode) {
  if (!countryCode) return false;
  const normalized = normalizeCountryCode(countryCode);
  return normalized in militaryIconCache;
}

/**
 * Get the military logo path for a country, optionally with service-specific icon
 * @param {string} countryCode - Country code
 * @param {string|null} operatorName - Operator name to detect service (optional)
 * @returns {string|null} Path to icon or null if not found
 */
export function getMilitaryLogo(countryCode, operatorName = null) {
  if (!countryCode) return null;

  const normalized = normalizeCountryCode(countryCode);
  const countryCache = militaryIconCache[normalized];

  if (!countryCache) return null;

  // Try to detect service from operator name
  if (operatorName) {
    const serviceCode = detectServiceFromOperator(operatorName);
    if (serviceCode && countryCache.services[serviceCode]) {
      return countryCache.services[serviceCode];
    }
  }

  // Fall back to default country icon
  return countryCache.default;
}

/**
 * Get all available service icons for a country
 * @param {string} countryCode - Country code
 * @returns {Object} Object with service codes as keys and paths as values
 */
export function getServiceIcons(countryCode) {
  if (!countryCode) return {};

  const normalized = normalizeCountryCode(countryCode);
  const countryCache = militaryIconCache[normalized];

  if (!countryCache) return {};

  return { ...countryCache.services };
}

/**
 * Mark a service icon as unavailable (called when image fails to load)
 * @param {string} countryCode - Country code
 * @param {string} serviceCode - Service code
 */
export function markServiceIconUnavailable(countryCode, serviceCode) {
  const normalized = normalizeCountryCode(countryCode);
  const countryCache = militaryIconCache[normalized];

  if (countryCache && countryCache.services[serviceCode]) {
    delete countryCache.services[serviceCode];
  }
}

/**
 * Get the cache status for debugging
 * @returns {Object} Current cache state
 */
export function getMilitaryIconCacheStatus() {
  return {
    initialized: cacheInitialized,
    countries: Object.keys(militaryIconCache),
    cache: { ...militaryIconCache },
  };
}

// Initialize cache synchronously on module load for immediate availability
initializeMilitaryIconCacheSync();

const militaryIconCacheExports = {
  hasMilitaryLogo,
  getMilitaryLogo,
  detectServiceFromOperator,
  getServiceIcons,
  markServiceIconUnavailable,
  initializeMilitaryIconCache,
  getMilitaryIconCacheStatus,
};

export default militaryIconCacheExports;
